import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { ShopContext } from './ShopContext';
import CartHelper from '../helpers/CartHelper';

export const CartContext = createContext();

export function CartProvider(props) {
  const { shop } = useContext(ShopContext);
  const shopId = shop?.shopId;
  const [cart, setCart] = useState({ products: [] });
  const [itemsCount, setItemsCount] = useState(0);

  const getCart = useCallback(async () => {
    const _cart = await CartHelper.getCart(shopId);
    setCart({ ..._cart });

    return _cart;
  }, [shopId]);

  const countCartItem = useCallback(
    async (providedCart = cart) => {
      const count = await CartHelper.cartItemCount(providedCart);
      setItemsCount(count);
    },
    [cart]
  );

  const addItemToCart = async (product) => {
    const quantityDelta = (shop?.options || {}).defaultQuantityStep || 1;
    const updatedCart = await CartHelper.addToCart(shopId, product, quantityDelta);
    setCart({ ...updatedCart });
    const count = await CartHelper.cartItemCount(updatedCart);
    setItemsCount(count);
  };

  const clearCartItems = async () => {
    CartHelper.clearCart(shopId);
    const cart = await getCart();
    await countCartItem(cart);
  };

  const removeItemFromCart = async (prodect) => {
    CartHelper.removeFromCart(shopId, prodect);
    await getCart();
    await countCartItem(cart);
  };

  const reduceItemFromCart = async (product) => {
    const quantityDelta = (shop?.options || {}).defaultQuantityStep || 1;
    const updatedCart = await CartHelper.reduceFromCart(shopId, product, quantityDelta);
    setCart({ ...updatedCart });
    const count = await CartHelper.cartItemCount(updatedCart);
    setItemsCount(count);
  };

  const setQtyInCart = async (product, quantity) => {
    await CartHelper.setQtyInCart(shopId, product, quantity);
    await getCart();
    await countCartItem(cart);
  };

  const reOrder = async (orderShopId, orderId) => {
    // If shopId is provided but doesn't match current shop, we might need to handle it.
    // But usually re-order is for the current shop.
    CartHelper.reOrder(orderShopId || shopId, orderId);
    await getCart();
    await countCartItem(cart);
  };

  useEffect(() => {
    setCart(null);
    setItemsCount(0);
    if (!shopId) return;
    getCart();
  }, [getCart, shopId]);

  useEffect(() => {
    countCartItem();
  }, [cart, countCartItem]);

  return (
    <CartContext.Provider
      value={{
        cart,
        itemsCount,
        addItemToCart,
        clearCartItems,
        removeItemFromCart,
        reduceItemFromCart,
        setQtyInCart,
        reOrder,
      }}
    >
      {props.children}
    </CartContext.Provider>
  );
}
